import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './PWAInstallPrompt.module.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const STORAGE_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface PWAInstallPromptProps {
  /** Optional callback when the app is installed */
  onInstall?: () => void;
  /** Optional callback when the prompt is dismissed */
  onDismiss?: () => void;
}

export function PWAInstallPrompt({ onInstall, onDismiss }: PWAInstallPromptProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Check if user has previously dismissed the prompt
  const isDismissed = useCallback(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) return false;

      const dismissedTime = parseInt(dismissed, 10);
      const now = Date.now();

      // If dismiss duration has passed, allow showing again
      if (now - dismissedTime > DISMISS_DURATION_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }, []);

  // Check if app is already installed
  const isInstalled = useCallback(() => {
    // Check if running in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // iOS Safari
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) {
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    // Don't show if already installed or previously dismissed
    if (isInstalled() || isDismissed()) {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      // Prevent the default browser install prompt
      event.preventDefault();

      // Store the event for later use
      setDeferredPrompt(event as BeforeInstallPromptEvent);

      // Show our custom prompt after a short delay
      setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    };

    const handleAppInstalled = () => {
      setIsVisible(false);
      setDeferredPrompt(null);
      onInstall?.();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, isDismissed, onInstall]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    try {
      // Show the native install prompt
      await deferredPrompt.prompt();

      // Wait for user choice
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsVisible(false);
        onInstall?.();
      }
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // Ignore localStorage errors
    }

    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={styles.container}
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          role="dialog"
          aria-labelledby="pwa-install-title"
          aria-describedby="pwa-install-description"
        >
          <div className={styles.content}>
            <div className={styles.iconWrapper} aria-hidden="true">
              <svg
                className={styles.icon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </div>

            <div className={styles.text}>
              <h3 id="pwa-install-title" className={styles.title}>
                Install Time Blocking
              </h3>
              <p id="pwa-install-description" className={styles.description}>
                Add to your home screen for quick access and offline support.
              </p>
            </div>

            <div className={styles.actions}>
              <button
                className={styles.installButton}
                onClick={handleInstall}
                disabled={isInstalling}
                aria-busy={isInstalling}
              >
                {isInstalling ? 'Installing...' : 'Install'}
              </button>
              <button
                className={styles.dismissButton}
                onClick={handleDismiss}
                aria-label="Dismiss install prompt"
              >
                Not now
              </button>
            </div>
          </div>

          <button
            className={styles.closeButton}
            onClick={handleDismiss}
            aria-label="Close install prompt"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PWAInstallPrompt;
